import { useTheme } from '@emotion/react';
import {
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  memo,
  type ReactNode,
  useContext,
} from 'react';

import { Typography } from '../../../general/typography/Typography';
import { RadioGroupContext } from '../../radio/contexts/RadioGroup.context';
import { RadioControl } from '../../radio-control/RadioControl';
import styles from './Radio.styles';

export type RadioProps = Omit<HTMLProps<HTMLInputElement>, 'ref' | 'label'> & {
  /** Label of the radio. */
  label: ReactNode;
};

export const RadioBase = forwardRef(
  (
    { value, label, disabled = false, ...props }: RadioProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const theme = useTheme();
    const { value: selectedValue, onChange } = useContext(RadioGroupContext);

    const checked = selectedValue === value;

    return (
      // biome-ignore lint/a11y/noLabelWithoutControl: RadioControl input is nested inside the label
      <label css={styles.container(theme, disabled)}>
        <div css={styles.radio}>
          <RadioControl
            {...props}
            ref={ref}
            value={value}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
          />
        </div>

        <Typography variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR} css={styles.label}>
          {label}
        </Typography>
      </label>
    );
  },
);

RadioBase.displayName = 'Radio';

export const Radio = memo(RadioBase);
